/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import Typed from 'react-typed';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import RocketIllustration from '../../../../../svg/illustrations/Rocket';
import jwtDecode from 'jwt-decode';
import {DecodedToken} from '../../../../../types/token';
import {Authority} from '../../../../../utils/enums';
import {useToken} from '../../../../../utils/useToken';
import {useTranslation} from 'react-i18next';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import YouTubeIcon from '@mui/icons-material/YouTube';
import TelegramIcon from '@mui/icons-material/Telegram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import IconButton from '@mui/material/IconButton';

const Hero = () => {
    const {t} = useTranslation();
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'), {
        defaultMatches: true,
    });

    const {token} = useToken();
    const decoded: DecodedToken | null = token ? jwtDecode(token.access_token) : null;

    function cabinetOrLoginLink() {
        if (!decoded) {
            return "/page-login"
        }

        switch (decoded.authorities[decoded.authorities.length - 1]) {
            case Authority.USER:
                return "/account-introduction"
            case Authority.MODERATOR:
                return "/moderator-introduction"
            case Authority.ADMIN:
                return "/admin-introduction"

            default:
                return "/account-introduction"
        }
    }

    return (
        <Box id="hero" sx={{ position: 'relative', minHeight: 'calc(100vh - 56px)', overflow: 'hidden' }}>
            {/* Full background video with bluish tone */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 0,
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 50, 100, 0.4)', // Bluish overlay
                        zIndex: 1
                    }
                }}
            >
                <video 
                    src={process.env.PUBLIC_URL + '/bg1.mp4'} 
                    autoPlay 
                    loop 
                    muted 
                    style={{ 
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                    }}
                    ref={(el) => {
                        if (el) {
                            el.playbackRate = 0.5; // Slow down the video to half speed
                        }
                    }}
                />
            </Box>

            {/* Content overlay */}
            <Grid container spacing={4} className="isolate-opacity" sx={{ position: 'relative', zIndex: 2, minHeight: '100vh', paddingLeft: '100px', paddingRight: '100px' }}>
                <Grid item container alignItems={'center'} xs={12} md={6}>
                    <Box data-aos={isMd ? 'fade-right' : 'fade-up'} sx={{ paddingLeft: 8 }}>
                        <Box marginBottom={2} className="isolate-opacity">
                            <Typography
                                variant="h3"
                                color="white"
                                className="force-opaque"
                                sx={{
                                    fontWeight: 700,
                                }}
                            >
                                {t("home_page.hero.platform_title")}
                                <br/>
                                <Typography
                                    sx={{color:"#528ed5"}}
                                    component={'span'}
                                    variant={'inherit'}
                                    className="force-opaque"
                                >
                                    {t("home_page.hero.platform_subtitle")}
                                    {/*<Typed*/}
                                    {/*    strings={t("home_page.hero.text3").split(",")}*/}
                                    {/*    typeSpeed={80}*/}
                                    {/*    loop={true}*/}
                                    {/*/>*/}
                                </Typography>
                               <Box py={2}/>
                                {/*<Typography color="white" py={0.5}*/}
                                {/*            className="force-opaque">*/}
                                {/*   {t("home_page.hero.platform_description1")}*/}

                                {/*</Typography>*/}
                                {/*<Typography color="white" py={0.5}*/}
                                {/*            className="force-opaque">*/}
                                {/*    {t("home_page.hero.platform_description2")}*/}

                                {/*</Typography>*/}
                                {/*<Typography color="white" py={0.5}*/}
                                {/*            className="force-opaque">*/}
                                {/*    {t("home_page.hero.platform_description3")}*/}

                                {/*</Typography>*/}
                            </Typography>
                        </Box>
                        <Box
                            display="flex"
                            flexDirection={{xs: 'column', sm: 'row'}}
                            alignItems={{xs: 'stretched', sm: 'flex-start'}}
                        >
                            <Button
                                component={'a'}
                                variant="contained"
                                color="primary"
                                size="large"
                                fullWidth={!isMd}
                                className="isolate-opacity"
                                href={cabinetOrLoginLink()}
                            >
                                <span className="force-opaque">{token ? t("pages.cabinet") : t("pages.login")}</span>
                            </Button>
                            <Box
                                marginTop={{xs: 2, sm: 0}}
                                marginLeft={{sm: 2}}
                                width={{xs: '100%', md: 'auto'}}
                            >
                                <Button
                                    component={'a'}
                                    href={'/page-signup'}
                                    variant="outlined"
                                    color="primary"
                                    size="large"
                                    fullWidth={!isMd}
                                    className="isolate-opacity"
                                    sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', color: 'white', borderColor: 'white' }}
                                >
                                    <span className="force-opaque">{t("pages.signup")}</span>
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={12} md={6} sx={{ height: '100%' }}>
                    {/* This grid item is kept for layout purposes but doesn't contain the video anymore */}
                </Grid>
            </Grid>

            {/* Social Media Icons */}
            <Box
                className="isolate-opacity"
                sx={{
                    position: 'absolute',
                    bottom: 20,
                    left: 20,
                    zIndex: 3,
                    display: 'flex',
                    gap: 1
                }}
            >
                <IconButton component="a" href="https://facebook.com" target="_blank" className="force-opaque" sx={{ color: 'white' }}>
                    <FacebookIcon />
                </IconButton>
                <IconButton component="a" href="https://twitter.com" target="_blank" className="force-opaque" sx={{ color: 'white' }}>
                    <TwitterIcon />
                </IconButton>
                <IconButton component="a" href="https://linkedin.com" target="_blank" className="force-opaque" sx={{ color: 'white' }}>
                    <LinkedInIcon />
                </IconButton>
                <IconButton component="a" href="https://youtube.com" target="_blank" className="force-opaque" sx={{ color: 'white' }}>
                    <YouTubeIcon />
                </IconButton>
                <IconButton component="a" href="https://telegram.org" target="_blank" className="force-opaque" sx={{ color: 'white' }}>
                    <TelegramIcon />
                </IconButton>
                <IconButton component="a" href="https://whatsapp.com" target="_blank" className="force-opaque" sx={{ color: 'white' }}>
                    <WhatsAppIcon />
                </IconButton>
            </Box>
        </Box>
    );
};

export default Hero;
